import React, { Component, ReactNode } from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onRetry?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

class ChunkErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: NodeJS.Timeout | null = null;
  private readonly maxRetries = 3;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if it's a chunk loading error
    const isChunkLoadError = error.message.includes('Loading chunk') ||
                           error.message.includes('Loading CSS chunk') ||
                           error.name === 'ChunkLoadError';

    if (isChunkLoadError) {
      return {
        hasError: true,
        error,
        retryCount: 0
      };
    }

    // Re-throw non-chunk errors
    throw error;
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Chunk loading error caught by boundary:', error, errorInfo);

    // Report to error tracking service in production
    if (process.env.NODE_ENV === 'production') {
      this.reportError(error, errorInfo);
    }

    // Auto-retry for chunk loading errors
    if (this.state.retryCount < this.maxRetries) {
      this.scheduleRetry();
    }
  }

  private reportError = (error: Error, errorInfo: React.ErrorInfo) => {
    // This would integrate with your error tracking service (Sentry, etc.)
    console.error('Error reported to tracking service:', { error, errorInfo });
  };

  private scheduleRetry = () => {
    // Progressive backoff: 1s, 2s, 4s
    const delay = Math.pow(2, this.state.retryCount) * 1000;

    this.retryTimeoutId = setTimeout(() => {
      this.handleRetry();
    }, delay);
  };

  private handleRetry = () => {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }

    this.setState(prevState => ({
      hasError: false,
      error: null,
      retryCount: prevState.retryCount + 1
    }));

    // Call custom retry handler if provided
    if (this.props.onRetry) {
      this.props.onRetry();
    }

    // Force reload the page if all retries failed
    if (this.state.retryCount >= this.maxRetries) {
      console.warn('Max retries reached, reloading page...');
      window.location.reload();
    }
  };

  private handleManualRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      retryCount: 0
    });

    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  render() {
    if (this.state.hasError) {
      // Show custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-dark-950 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-dark-900 border border-gray-700 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>

            <h2 className="text-xl font-bold text-white mb-2">
              Loading Error
            </h2>

            <p className="text-gray-400 mb-6">
              {this.state.retryCount < this.maxRetries
                ? `Retrying automatically... (${this.state.retryCount + 1}/${this.maxRetries})`
                : 'Failed to load page component. This might be due to a network issue or updated code.'
              }
            </p>

            {this.state.retryCount >= this.maxRetries && (
              <div className="space-y-3">
                <button
                  onClick={this.handleManualRetry}
                  className="w-full px-4 py-3 bg-neon-green text-black rounded-lg font-semibold hover:bg-green-400 transition-colors flex items-center justify-center"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </button>

                <button
                  onClick={() => window.location.reload()}
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors"
                >
                  Reload Page
                </button>
              </div>
            )}

            <p className="text-xs text-gray-500 mt-4">
              Error: {this.state.error?.message}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;